import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';
import type {
  ModerationAction,
  ModerationActorNote,
  ModerationCapability,
  ModerationFinding,
  ModerationFindingDetail,
  ModerationRule,
  ModerationSeverity,
  FindingState,
} from '../api/types.js';
import { Alert } from '../components/Alert.js';
import { EmptyState } from '../components/EmptyState.js';
import { HelpHint } from '../components/Tooltip.js';
import { ModerationCapabilities } from '../components/ModerationCapabilities.js';
import { ACTION_LABEL, CAPABILITY_LABEL } from '../components/moderation-labels.js';
import { PlatformIcon } from '../components/PlatformIcon.js';
import { RuleEditorDialog } from '../components/RuleEditorDialog.js';
import { useSession } from '../auth/session-context.js';
import { useChatSocket } from '../chat/use-chat-socket.js';
import './moderator.css';

/**
 * Connaro Moderator (ARCHITECTURE.md §64, §98).
 *
 * A queue of findings, a detail panel, and the rules that produce them.
 *
 * WHAT THIS PAGE WILL NOT DO: decide anything. Every button here is enabled or
 * disabled by an answer the SERVER gave — the capability report for the
 * connection the finding came from, and the permissions on the session. The
 * Console never infers that an action is possible, and when one is not it says
 * the provider's own reason rather than greying a button out in silence. §65
 * forbids meaning carried by appearance alone, and "this is grey" is exactly
 * that.
 *
 * Findings arrive live over the same WebSocket that carries chat, on their own
 * channel and only for roles holding `moderation.view`. When the gateway is not
 * relaying them the page says so, because a queue that is quiet because nothing
 * is wrong looks identical to one that is quiet because it is broken.
 */

const SEVERITY_ORDER: ModerationSeverity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const STATE_FILTERS: { value: string; label: string; states: FindingState[] }[] = [
  { value: 'open', label: 'Needs review', states: ['OPEN'] },
  { value: 'acknowledged', label: 'Acknowledged', states: ['ACKNOWLEDGED'] },
  { value: 'resolved', label: 'Resolved', states: ['ACTIONED', 'DISMISSED'] },
  { value: 'all', label: 'Everything', states: [] },
];

/** Default timeout offered, in seconds. Ten minutes: long enough to cool off. */
const DEFAULT_TIMEOUT_SECONDS = 600;

function severityClass(prefix: string, severity: ModerationSeverity): string {
  return `${prefix} ${prefix}--${severity.toLowerCase()}`;
}

function timeOf(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString();
}

export function ModeratorPage(): React.JSX.Element {
  const { activeWorkspace, can } = useSession();
  const workspaceId = activeWorkspace?.id ?? null;

  /*
   * Render-time only, and the context says so.
   *
   * Hiding a button the actor cannot use is courtesy; the API refuses the
   * request regardless of what this page rendered (§4.2). Nothing below treats
   * these as a security boundary.
   */
  const mayResolve = can('moderation.resolve');
  const mayAct = can('moderation.act');
  const mayManageRules = can('moderation.rules.manage');

  const [findings, setFindings] = useState<ModerationFinding[] | null>(null);
  const [openCounts, setOpenCounts] = useState<Record<ModerationSeverity, number> | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ModerationFindingDetail | null>(null);
  const [rules, setRules] = useState<ModerationRule[] | null>(null);
  const [filter, setFilter] = useState('open');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<ModerationRule | 'new' | null>(null);
  const [entitled, setEntitled] = useState(true);

  /**
   * Arriving from Chat's "Open in Moderator" link (§98, module brief §17).
   *
   * A viewer filter, not a finding id: the operator clicked a person, and what
   * they want is everything Connaro has flagged about that person — which is
   * usually more than the one line that prompted the click.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedActor = searchParams.get('platformUserId');

  const socket = useChatSocket(workspaceId);

  /** Nothing open means the strip below has nothing to compare. */
  const totalOpen = useMemo(
    () =>
      openCounts === null
        ? 0
        : SEVERITY_ORDER.reduce((sum, severity) => sum + openCounts[severity], 0),
    [openCounts],
  );

  const activeStates = useMemo(
    () => STATE_FILTERS.find((entry) => entry.value === filter)?.states ?? [],
    [filter],
  );

  const loadFindings = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeStates.length > 0) params.set('state', activeStates.join(','));
      if (focusedActor !== null && focusedActor !== '') {
        params.set('platformUserId', focusedActor);
      }
      const query = params.size === 0 ? '' : `?${params.toString()}`;
      const data = await apiRequest<{
        findings: ModerationFinding[];
        openCounts: Record<ModerationSeverity, number>;
      }>(`/moderation/findings${query}`);

      setFindings(data.findings);
      setOpenCounts(data.openCounts);
      setError(null);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'ENTITLEMENT_REQUIRED') {
        setEntitled(false);
        setFindings([]);
        return;
      }
      setFindings([]);
      setError(
        cause instanceof ApiError ? cause.message : 'Connaro could not load the moderation queue.',
      );
    }
  }, [activeStates, focusedActor]);

  const loadRules = useCallback(async () => {
    try {
      const data = await apiRequest<{ rules: ModerationRule[] }>('/moderation/rules');
      setRules(data.rules);
    } catch {
      setRules([]);
    }
  }, []);

  useEffect(() => {
    void loadFindings();
  }, [loadFindings]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  /*
   * Live findings.
   *
   * Prepended to the list rather than triggering a refetch: a refetch on every
   * arrival would reorder the queue under the operator's cursor while they are
   * reading a finding, which is precisely when they least want the page to
   * move. The counts are refreshed, since those are a summary and cheap to be
   * right about.
   */
  useEffect(
    () =>
      socket.onFinding((finding) => {
        setFindings((current) => {
          if (current === null) return current;
          if (current.some((entry) => entry.id === finding.id)) return current;
          // Only the "needs review" and "everything" views should grow: a
          // brand-new finding is OPEN and does not belong in a resolved list.
          if (filter !== 'open' && filter !== 'all') return current;
          return [finding, ...current];
        });

        setOpenCounts((current) =>
          current === null
            ? current
            : { ...current, [finding.severity]: current[finding.severity] + 1 },
        );
      }),
    [socket, filter],
  );

  const openFinding = useCallback(async (id: string) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const data = await apiRequest<ModerationFindingDetail>(`/moderation/findings/${id}`);
      setDetail(data);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Connaro could not open that finding.');
    }
  }, []);

  const resolve = useCallback(
    async (id: string, how: 'acknowledge' | 'dismiss') => {
      setBusy(true);
      try {
        await apiRequest(`/moderation/findings/${id}/${how}`, { method: 'POST' });
        setNotice({
          tone: 'success',
          text: how === 'dismiss' ? 'Finding dismissed.' : 'Finding acknowledged.',
        });
        await loadFindings();
        if (selectedId === id) await openFinding(id);
      } catch (cause) {
        setNotice({
          tone: 'error',
          text: cause instanceof ApiError ? cause.message : 'That did not work.',
        });
      } finally {
        setBusy(false);
      }
    },
    [loadFindings, openFinding, selectedId],
  );

  const act = useCallback(
    async (finding: ModerationFinding, capability: ModerationCapability) => {
      setBusy(true);
      try {
        await apiRequest('/moderation/actions', {
          method: 'POST',
          body: {
            findingId: finding.id,
            action: capability.action,
            ...(capability.action === 'user.timeout'
              ? { durationSeconds: DEFAULT_TIMEOUT_SECONDS }
              : {}),
          },
        });

        setNotice({ tone: 'success', text: `${ACTION_LABEL[capability.action]} succeeded.` });
        await loadFindings();
        await openFinding(finding.id);
      } catch (cause) {
        /*
         * The provider's refusal, verbatim from the server.
         *
         * Not "something went wrong": an operator whose Twitch connection needs
         * reauthorizing can fix that in a minute if they are told, and cannot
         * fix anything if they are not.
         */
        setNotice({
          tone: 'error',
          text:
            cause instanceof ApiError
              ? cause.message
              : `Connaro could not reach ${finding.platform}.`,
        });
      } finally {
        setBusy(false);
      }
    },
    [loadFindings, openFinding],
  );

  if (!entitled) {
    return (
      <section className="panel" aria-labelledby="moderator-heading">
        <div className="panel__header">
          <h1 className="panel__title" id="moderator-heading">
            Connaro Moderator
          </h1>
        </div>
        <div className="panel__body">
          <EmptyState title="Not included in this plan">
            <p>
              Connaro Moderator watches every connected chat for the things you tell it to look for,
              and gives your team one place to deal with them. It is included from the Pro plan
              upwards.
            </p>
          </EmptyState>
        </div>
      </section>
    );
  }

  return (
    <>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {notice === null ? null : <Alert tone={notice.tone}>{notice.text}</Alert>}

      <div className={`moderator${selectedId === null ? '' : ' moderator--split'}`}>
        <div>
          <section className="panel" aria-labelledby="moderator-heading">
            <div className="panel__header">
              <h1 className="panel__title" id="moderator-heading">
                Connaro Moderator
              </h1>
              <HelpHint
                tip="Rules flag messages. Nothing is ever deleted or banned automatically — every action here is one you take."
                label="How moderation works"
              />
            </div>

            <div className="panel__body">
              {/*
               * The severity strip is a COMPARISON, and it only appears when
               * there is something to compare.
               *
               * All zero, it was four unlabelled numbers sitting above an
               * empty state that already says "Nothing to review" — the first
               * thing an operator asked was what the zeros meant. A zero
               * still earns its place once anything is open, because "0
               * critical" is worth reading beside "7 high".
               *
               * Labelled ONCE by a real heading rather than by a hidden span
               * repeated into every item, so the label a screen reader hears
               * is the label a sighted operator reads.
               */}
              {openCounts === null || totalOpen === 0 ? null : (
                <div role="group" aria-labelledby="open-findings-heading">
                  <h2 className="detail__section-title" id="open-findings-heading">
                    Open findings
                  </h2>
                  <ul className="moderator__counts">
                    {SEVERITY_ORDER.map((severity) => (
                      <li
                        key={severity}
                        className={severityClass('moderator__count finding', severity)}
                      >
                        <span>{severity.charAt(0) + severity.slice(1).toLowerCase()}</span>
                        <span className="moderator__count-value">{openCounts[severity]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {focusedActor === null || focusedActor === '' ? null : (
                <div className="moderator__toolbar">
                  <span className="status status--idle">Showing one viewer only</span>
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => {
                      // Clears the filter without a navigation, so the browser
                      // Back button still returns to Chat rather than cycling
                      // through filter states.
                      setSearchParams({}, { replace: true });
                    }}
                  >
                    Show everyone
                  </button>
                </div>
              )}

              <div className="moderator__toolbar">
                <label className="field__label" htmlFor="finding-filter">
                  Show
                </label>
                <select
                  id="finding-filter"
                  className="field__input"
                  value={filter}
                  onChange={(event) => {
                    setFilter(event.target.value);
                    setSelectedId(null);
                  }}
                >
                  {STATE_FILTERS.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </select>

                {/*
                 * Said in words, never by an icon alone. If findings are not
                 * being pushed, the operator must know the list is only as
                 * fresh as their last refresh.
                 */}
                <span className="rule__spacer" />
                <span className={`status ${socket.moderationLive ? 'status--ok' : 'status--idle'}`}>
                  <span className="status__dot" aria-hidden="true" />
                  {socket.moderationLive ? 'Live' : 'Not live — refresh to update'}
                </span>
              </div>

              {findings === null ? (
                <p>Loading…</p>
              ) : findings.length === 0 ? (
                <EmptyState title="Nothing to review">
                  <p>
                    {rules !== null && rules.length === 0
                      ? 'No rules are set up yet, so nothing is being flagged. Add a rule below to start.'
                      : 'No messages have matched your rules. This is the state you want.'}
                  </p>
                </EmptyState>
              ) : (
                <ul className="findings">
                  {findings.map((finding) => (
                    <li key={finding.id}>
                      <button
                        type="button"
                        className={severityClass('finding', finding.severity)}
                        aria-current={selectedId === finding.id}
                        onClick={() => {
                          void openFinding(finding.id);
                        }}
                      >
                        <span className="finding__head">
                          <span className={severityClass('severity', finding.severity)}>
                            {finding.severity}
                          </span>
                          <PlatformIcon
                            platform={finding.platform}
                            displayName={finding.platform}
                          />
                          <span className="finding__actor">
                            {finding.actorDisplayName ?? finding.actorUsername ?? 'Unknown'}
                          </span>
                          <span className="finding__time">{timeOf(finding.occurredAt)}</span>
                        </span>

                        {/* Untrusted chat text. Interpolated, never HTML. */}
                        <p className="finding__text">{finding.messageText ?? '(no text)'}</p>
                        <p className="finding__why">
                          {finding.reason}
                          {finding.state === 'OPEN' ? '' : ` · ${finding.state.toLowerCase()}`}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <RulesPanel
            rules={rules}
            mayManage={mayManageRules}
            onEdit={setEditing}
            onChanged={() => {
              void loadRules();
            }}
          />

          {/*
           * Below the rules, because it answers the question that follows
           * writing one: "and what can I actually do when this fires?"
           */}
          <ModerationCapabilities />
        </div>

        {selectedId === null ? null : (
          <FindingDetail
            detail={detail}
            busy={busy}
            mayResolve={mayResolve}
            mayAct={mayAct}
            onResolve={resolve}
            onAct={act}
            onClose={() => {
              setSelectedId(null);
              setDetail(null);
            }}
          />
        )}
      </div>

      {editing === null ? null : (
        <RuleEditorDialog
          rule={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={() => {
            setEditing(null);
            void loadRules();
          }}
        />
      )}
    </>
  );
}

// --- detail ----------------------------------------------------------------

function FindingDetail({
  detail,
  busy,
  mayResolve,
  mayAct,
  onResolve,
  onAct,
  onClose,
}: {
  detail: ModerationFindingDetail | null;
  busy: boolean;
  mayResolve: boolean;
  mayAct: boolean;
  onResolve: (id: string, how: 'acknowledge' | 'dismiss') => Promise<void>;
  onAct: (finding: ModerationFinding, capability: ModerationCapability) => Promise<void>;
  onClose: () => void;
}): React.JSX.Element {
  if (detail === null) {
    return (
      <section className="panel" aria-label="Finding detail">
        <div className="panel__body">
          <p>Loading…</p>
        </div>
      </section>
    );
  }

  const { finding, history, notes, actions, capabilities } = detail;
  const resolved = finding.state !== 'OPEN' && finding.state !== 'ACKNOWLEDGED';

  return (
    <section className="panel" aria-labelledby="detail-heading">
      <div className="panel__header">
        <h2 className="panel__title" id="detail-heading">
          {finding.actorDisplayName ?? finding.actorUsername ?? 'Unknown viewer'}
        </h2>
        <button type="button" className="btn btn--sm" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="panel__body">
        {/* Untrusted content, rendered as text (§21). */}
        <p className="detail__message">{finding.messageText ?? '(no text)'}</p>

        <dl className="detail__meta">
          <dt>Rule</dt>
          <dd>{finding.ruleName}</dd>
          <dt>Why</dt>
          <dd>{finding.reason}</dd>
          <dt>Platform</dt>
          <dd>{finding.platform}</dd>
          <dt>When</dt>
          <dd>{new Date(finding.occurredAt).toLocaleString()}</dd>
          <dt>State</dt>
          <dd>{finding.state}</dd>
        </dl>

        <div className="detail__actions">
          {mayResolve && !resolved ? (
            <>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => {
                  void onResolve(finding.id, 'acknowledge');
                }}
              >
                Acknowledge
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => {
                  void onResolve(finding.id, 'dismiss');
                }}
              >
                Dismiss
              </button>
            </>
          ) : null}
        </div>

        <div className="detail__section">
          <h3 className="detail__section-title">Act on {finding.platform}</h3>

          {!mayAct ? (
            <p className="detail__unavailable">
              Your role can review findings but not act on them.
            </p>
          ) : (
            capabilities.map((capability) => {
              const available = capability.state === 'SUPPORTED';
              const needsMessage = capability.action === 'message.delete';
              const missingTarget = needsMessage
                ? finding.platformMessageId === null
                : finding.platformUserId === null;

              return (
                <div key={capability.action}>
                  <button
                    type="button"
                    className={`btn${capability.action === 'user.ban' ? ' btn--danger' : ''}`}
                    disabled={busy || !available || missingTarget || resolved}
                    onClick={() => {
                      void onAct(finding, capability);
                    }}
                  >
                    {ACTION_LABEL[capability.action]}
                  </button>

                  {/*
                   * What the action WILL DO, before it is clicked.
                   *
                   * The adapters have always described this and nothing ever
                   * rendered it, so an operator saw a bare "Ban" button. On
                   * Discord that button bans a member from the ENTIRE SERVER —
                   * every channel, not the one the message was in — and the
                   * only place that was written down was a comment in a file
                   * they will never read.
                   *
                   * Scope is not something to discover afterwards.
                   */}
                  {available && !missingTarget ? (
                    <p className="detail__effect">{capability.note}</p>
                  ) : (
                    <p className="detail__unavailable">
                      {missingTarget && available
                        ? needsMessage
                          ? 'The platform did not give this message an id, so it cannot be deleted.'
                          : 'Connaro does not have a provider id for this viewer.'
                        : `${CAPABILITY_LABEL[capability.state]}. ${capability.note}`}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>

        {actions.length === 0 ? null : (
          <div className="detail__section">
            <h3 className="detail__section-title">What has been done</h3>
            <ul className="detail__list">
              {actions.map((action: ModerationAction) => (
                <li key={action.id}>
                  {ACTION_LABEL[action.actionType]} — {action.status.toLowerCase()}
                  {action.failureMessage === null ? '' : `: ${action.failureMessage}`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {notes.length === 0 ? null : (
          <div className="detail__section">
            <h3 className="detail__section-title">Notes about this viewer</h3>
            <ul className="detail__list">
              {notes.map((note: ModerationActorNote) => (
                <li key={note.id}>{note.note}</li>
              ))}
            </ul>
          </div>
        )}

        {history.length === 0 ? null : (
          <div className="detail__section">
            <h3 className="detail__section-title">
              Earlier findings for this viewer ({history.length})
            </h3>
            <ul className="detail__list">
              {history.slice(0, 10).map((entry) => (
                <li key={entry.id}>
                  <span className={severityClass('severity', entry.severity)}>
                    {entry.severity}
                  </span>{' '}
                  {entry.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

// --- rules -----------------------------------------------------------------

const KIND_LABEL: Record<ModerationRule['kind'], string> = {
  blocked_terms: 'Blocked words',
  regex: 'Pattern',
  links: 'Links',
  repetition: 'Repeated messages',
  flood: 'Message flood',
};

function RulesPanel({
  rules,
  mayManage,
  onEdit,
  onChanged,
}: {
  rules: ModerationRule[] | null;
  mayManage: boolean;
  onEdit: (rule: ModerationRule | 'new') => void;
  onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);

  const remove = async (rule: ModerationRule): Promise<void> => {
    setBusy(rule.id);
    try {
      await apiRequest(`/moderation/rules/${rule.id}`, { method: 'DELETE' });
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="panel" aria-labelledby="rules-heading" style={{ marginTop: '1rem' }}>
      <div className="panel__header">
        <h2 className="panel__title" id="rules-heading">
          Rules
        </h2>
        <HelpHint
          tip="Rules decide what gets flagged. They never punish anyone on their own — a person always decides what happens next."
          label="What rules do"
        />
        {mayManage ? (
          <button
            type="button"
            className="btn btn--sm btn--primary rule__spacer"
            onClick={() => {
              onEdit('new');
            }}
          >
            Add rule
          </button>
        ) : null}
      </div>

      <div className="panel__body">
        {rules === null ? (
          <p>Loading…</p>
        ) : rules.length === 0 ? (
          <EmptyState title="No rules yet">
            <p>
              A rule watches every connected chat for one thing — a list of words, a link, someone
              repeating themselves. When it matches, the message appears above for you to decide
              about.
            </p>
          </EmptyState>
        ) : (
          <ul className="rules">
            {rules.map((rule) => (
              <li key={rule.id} className="rule">
                <span className="rule__name">{rule.name}</span>
                <span className="rule__kind">{KIND_LABEL[rule.kind]}</span>
                <span className={severityClass('severity', rule.severity)}>{rule.severity}</span>
                {rule.enabled ? null : <span className="rule__kind">Disabled</span>}

                {mayManage ? (
                  <span className="rule__spacer">
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => {
                        onEdit(rule);
                      }}
                    >
                      Edit
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn--sm btn--danger"
                      disabled={busy === rule.id}
                      onClick={() => {
                        void remove(rule);
                      }}
                    >
                      Delete
                    </button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
